import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { apiClient } from "@/api/apiClient";
import { m } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import MatchCard from "@/components/matches/MatchCard";
import OptimizedImage from "@/components/ui/OptimizedImage";
import { Capacitor } from "@capacitor/core";
import { openExternalUrl } from "@/utils";
import { toast } from "sonner";
import {
    Bell, BellRing, ExternalLink, Instagram, Loader2,
    Sparkles, ChevronLeft, ChevronRight,
    ArrowLeft, ShoppingBag, Flame, Tag, Filter, X, Heart
} from "lucide-react";
import FilterPanel from "@/components/swipe/FilterPanel";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useLikedProducts } from "@/hooks/useLikedProducts";

// ========================
// SUB-COMPONENTS
// ========================

function BrandHero({ brand, stats, userState, onToggleSubscription, isSubscribing }) {
    return (
        <div className="flex flex-col items-center text-center px-4 pt-6 pb-4">
            {/* Logo */}
            {brand.logo_url ? (
                <div className="w-24 mb-3">
                    <OptimizedImage src={brand.logo_url} alt={brand.name} className="w-full h-auto object-contain" width={120} />
                </div>
            ) : (
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-950/50 dark:to-yellow-950/50 flex items-center justify-center mb-3 shadow-lg border-2 border-white dark:border-gray-800">
                    <span className="text-2xl font-black text-amber-600 dark:text-amber-400">{brand.name?.charAt(0)}</span>
                </div>
            )}

            {/* Name */}
            <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{brand.name}</h1>

            {/* Description */}
            {brand.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 px-4 leading-relaxed">{brand.description}</p>
            )}

            {/* Stats row */}
            <div className="flex flex-wrap justify-center items-center gap-2 md:gap-4 text-xs text-gray-400 mb-3">
                <span><strong className="text-gray-700 dark:text-gray-300">{stats.totalProducts}</strong> produits</span>
                <span>•</span>
                <span><strong className="text-gray-700 dark:text-gray-300">{stats.subscriberCount}</strong> abonnés</span>
                {stats.newProducts > 0 && (
                    <>
                        <span>•</span>
                        <span className="text-amber-600 dark:text-amber-400 font-semibold">{stats.newProducts} nouveautés</span>
                    </>
                )}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2">
                <Button
                    onClick={onToggleSubscription}
                    disabled={isSubscribing}
                    className={`rounded-full px-5 text-sm font-semibold transition-all ${
                        userState.isSubscribed
                            ? "bg-amber-100 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-950/50 border border-amber-200 dark:border-amber-800"
                            : "bg-gradient-to-r from-amber-600 to-amber-700 text-white hover:from-amber-700 hover:to-amber-800 shadow-lg"
                    }`}
                    size="sm"
                >
                    {isSubscribing ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : userState.isSubscribed ? (
                        <BellRing className="w-4 h-4 mr-1" />
                    ) : (
                        <Bell className="w-4 h-4 mr-1" />
                    )}
                    {userState.isSubscribed ? "Abonné" : "Suivre"}
                </Button>

                {brand.website_url && (
                    <a
                        href={brand.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {

                            if (brand?.id) apiClient.entities.UserInteraction.create({ brand_id: brand.id, action: 'visit_marque_site' });

                            if (Capacitor.isNativePlatform()) {
                                openExternalUrl(brand.website_url, e);
                            }
                        }}
                    >
                        <Button variant="outline" size="sm" className="rounded-full text-xs dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
                            <ExternalLink className="w-3.5 h-3.5 mr-1" /> Site
                        </Button>
                    </a>
                )}
                {brand.instagram_url && (
                    <a
                        href={brand.instagram_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {

                            if (brand?.id) apiClient.entities.UserInteraction.create({ brand_id: brand.id, action: 'visit_marque_site' });

                            if (Capacitor.isNativePlatform()) {
                                openExternalUrl(brand.instagram_url, e);
                            }
                        }}
                    >
                        <Button variant="outline" size="sm" className="rounded-full px-2.5 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
                            <Instagram className="w-4 h-4" />
                        </Button>
                    </a>
                )}
            </div>
        </div>
    );
}

function NewProductsRadar({ count }) {
    if (!count || count <= 0) return null;
    return (
        <m.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mx-4 mb-3 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border border-amber-100 dark:border-amber-800 flex items-center gap-2"
        >
            <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <span className="text-sm text-amber-800 dark:text-amber-300 font-medium">
                <strong>{count}</strong> nouvelle{count > 1 ? "s" : ""} pièce{count > 1 ? "s" : ""} depuis votre dernière visite
            </span>
        </m.div>
    );
}

const TopProductsCarousel = BrandRadarSection;
function BrandRadarSection({ products, brandName }) {
    const scrollRef = useRef(null);
    const { isLiked, toggleLike } = useLikedProducts();

    if (!products || products.length === 0) return null;

    const scroll = (direction) => {
        if (scrollRef.current) {
            const { scrollLeft, clientWidth } = scrollRef.current;
            const scrollAmount = clientWidth * 0.75;
            scrollRef.current.scrollTo({
                left: direction === 'left' ? scrollLeft - scrollAmount : scrollLeft + scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className="py-4 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between px-4 mb-3">
                <div className="flex items-center gap-2">
                    <Flame className="w-4 h-4 text-amber-500" />
                    <h2 className="text-base font-bold text-gray-900 dark:text-white">Nouveautés</h2>
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 font-semibold border-none">
                        {products.length}
                    </Badge>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => scroll('left')}
                        className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Défiler à gauche"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => scroll('right')}
                        className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Défiler à droite"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
            <div
                ref={scrollRef}
                className="flex gap-3 overflow-x-auto px-4 pb-2"
                style={{ scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" }}
            >
                {products.map((product) => {
                    const liked = isLiked(product.id);
                    return (
                        <Link
                            key={product.id}
                            to={`/product?id=${product.id}`}
                            className="flex-shrink-0 w-36 group relative"
                            style={{ scrollSnapAlign: "start" }}
                        >
                            <div className="rounded-xl overflow-hidden shadow-md bg-white dark:bg-[hsl(222,20%,10%)] border border-gray-100 dark:border-gray-800 hover:shadow-lg transition-shadow">
                                <div className="aspect-[3/4] overflow-hidden bg-gray-100 dark:bg-gray-800 relative">
                                    <OptimizedImage
                                        src={product.images?.[0]}
                                        alt={product.name}
                                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                        width={160}
                                    />
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            toggleLike(product, e);
                                        }}
                                        className={`absolute top-1.5 right-1.5 z-10 p-1.5 rounded-full backdrop-blur-md transition-all shadow-sm flex items-center justify-center ${
                                            liked
                                                ? "bg-white/95 dark:bg-zinc-900/95 text-red-500 ring-1 ring-red-500/30"
                                                : "bg-black/35 text-white hover:bg-white/95 dark:hover:bg-zinc-900/95 hover:text-red-500"
                                        }`}
                                        title={liked ? "Retirer des coups de cœur" : "Ajouter aux coups de cœur"}
                                        aria-label={liked ? "Retirer des coups de cœur" : "Ajouter aux coups de cœur"}
                                    >
                                        <Heart
                                            size={12}
                                            className={`transition-colors duration-200 ${liked ? "fill-red-500 text-red-500" : "fill-transparent"}`}
                                        />
                                    </button>
                                </div>
                                <div className="p-2">
                                    <p className="text-xs font-semibold text-gray-900 dark:text-white line-clamp-1">{product.name}</p>
                                    <div className="flex items-center gap-1 mt-0.5">
                                        <span className="text-sm font-bold text-gray-900 dark:text-white">{product.price}€</span>
                                        {product.original_price && product.original_price > product.price && (
                                            <span className="text-[10px] text-gray-400 line-through">{product.original_price}€</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}

function MySizeFilterButton({ user, isAuthenticated, isActive, onToggle, disabled }) {
    const navigate = useNavigate();

    const hasSizes = user?.sizes && Object.values(user.sizes).some(Boolean);

    const handleClick = (e) => {
        e.stopPropagation();
        if (disabled) return;
        if (!isAuthenticated) {
            toast.error("🔒 Inscrivez-vous pour filtrer par votre taille", {
                action: {
                    label: "Créer un compte",
                    onClick: () => navigate("/register")
                }
            });
            return;
        }
        if (!hasSizes) {
            toast.error("⚠️ Ajoutez vos tailles dans votre profil pour utiliser ce filtre", {
                action: {
                    label: "Configurer",
                    onClick: () => navigate("/profile")
                }
            });
            return;
        }
        onToggle();
    };

    return (
        <div className="relative flex-shrink-0">
            <button
                disabled={disabled}
                onClick={handleClick}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    isActive
                        ? "bg-amber-600 text-white border-amber-600 shadow-md"
                        : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-amber-300 dark:hover:border-amber-600"
                } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            >
                <span>👗</span> Ma Taille
                {isActive && hasSizes && (
                    <span className="text-[10px] opacity-80">
                        ({Object.values(user.sizes).filter(Boolean).join(", ")})
                    </span>
                )}
            </button>
        </div>
    );
}

// ========================
// MAIN BRAND PAGE
// ========================

export default function BrandPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const observerRef = useRef(null);
    const loadMoreRef = useRef(null);
    const { completeTask } = useOnboarding();

    const [activeTab, setActiveTab] = useState("all");
    const [mySizesOnly, setMySizesOnly] = useState(false);
    const [onSaleOnly, setOnSaleOnly] = useState(false);
    const [page, setPage] = useState(1);
    const [allProducts, setAllProducts] = useState([]);
    const [hasMore, setHasMore] = useState(true);

    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        minPrice: 0,
        maxPrice: 10000,
        categories: [],
        subcategories: [],
        colors: [],
        brands: [slug], // Keep brand fixed to the slug
        sizes: [],
        inStockOnly: false,
        is_new: false,
    });

    useEffect(() => {
        completeTask('visit_brand_page');
        completeTask('view_brand_of_day');
    }, []);

    // Reset filters when slug changes
    useEffect(() => {
        setFilters(prev => ({ ...prev, brands: [slug] }));
    }, [slug]);

    // Auth state
    const { data: user } = useQuery({
        queryKey: ["me"],
        queryFn: () => apiClient.auth.me(),
        retry: false,
        staleTime: 1000 * 60 * 5,
    });
    const isAuthenticated = !!user;

    // Fetch dynamic category rules
    const { data: rulesData } = useQuery({
        queryKey: ['categoryRules'],
        queryFn: () => apiClient.entities.UserCategory.getSystemRules().catch(() => []),
        staleTime: 1000 * 60 * 5,
    });
    const rules = rulesData || [];

    // Fetch Global Metadata (Max Price, etc.)
    const { data: metadata } = useQuery({
        queryKey: ['metadata'],
        queryFn: () => apiClient.entities.Product.getMetadata(),
        staleTime: 1000 * 60 * 5,
    });

    // Brand page data
    const { data: brandData, isLoading, isFetching, error } = useQuery({
        queryKey: ["brandPage", slug, activeTab, mySizesOnly, onSaleOnly, filters, page],
        queryFn: () =>
            apiClient.brands.getBySlug(slug, {
                tab: activeTab,
                page,
                limit: 20,
                mySizesOnly,
                onSaleOnly,
                ...filters,
                brands: [slug], // Ensure it's always this brand
                is_new: activeTab === "new" || filters.is_new
            }),
        enabled: !!slug,
        placeholderData: keepPreviousData,
    });

    // Reset products when filters change (but keep existing products visible until new ones arrive)
    useEffect(() => {
        setPage(1);
        setHasMore(true);
    }, [activeTab, mySizesOnly, onSaleOnly, filters, slug]);

    // Accumulate products for infinite scroll
    useEffect(() => {
        if (brandData?.products) {
            if (page === 1) {
                // Replace products when page resets (tab/filter change)
                setAllProducts(brandData.products);
            } else {
                setAllProducts((prev) => {
                    const existingIds = new Set(prev.map((p) => p.id));
                    const newOnes = brandData.products.filter((p) => !existingIds.has(p.id));
                    return [...prev, ...newOnes];
                });
            }
            setHasMore(brandData.products.length >= 20);
        }
    }, [brandData, page]);

    // Infinite scroll observer
    const handleObserver = useCallback(
        (entries) => {
            const [entry] = entries;
            if (entry.isIntersecting && hasMore && !isLoading) {
                setPage((prev) => prev + 1);
            }
        },
        [hasMore, isLoading]
    );

    useEffect(() => {
        if (observerRef.current) observerRef.current.disconnect();
        observerRef.current = new IntersectionObserver(handleObserver, { threshold: 0.1 });
        if (loadMoreRef.current) observerRef.current.observe(loadMoreRef.current);
        return () => observerRef.current?.disconnect();
    }, [handleObserver]);

    // Subscription toggle
    const subscribeMutation = useMutation({
        mutationFn: () => apiClient.notifications.subscribe(brandData?.brand?.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["brandPage", slug] });
            completeTask('subscribe_brand');
        },
    });
    const unsubscribeMutation = useMutation({
        mutationFn: () => apiClient.notifications.unsubscribe(brandData?.brand?.id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["brandPage", slug] }),
    });

    const handleToggleSubscription = () => {
        if (!isAuthenticated) {
            navigate("/register");
            return;
        }
        if (brandData?.userState?.isSubscribed) {
            unsubscribeMutation.mutate();
        } else {
            subscribeMutation.mutate();
        }
    };

    // Schema.org injection
    useEffect(() => {
        if (brandData?.schemaOrg) {
            const existingScript = document.getElementById("brand-schema-org");
            if (existingScript) existingScript.remove();

            const script = document.createElement("script");
            script.id = "brand-schema-org";
            script.type = "application/ld+json";
            script.textContent = JSON.stringify(brandData.schemaOrg);
            document.head.appendChild(script);

            // Update page title
            document.title = `${brandData.brand.name} - Pépite`;

            return () => {
                const el = document.getElementById("brand-schema-org");
                if (el) el.remove();
                document.title = "Pépite";
            };
        }
    }, [brandData?.schemaOrg]);

    // Loading state
    if (isLoading && page === 1 && !brandData) {
        return (
            <div className="h-screen flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin" />
                    <p className="text-gray-500 text-sm">Chargement...</p>
                </div>
            </div>
        );
    }

    // 404
    if (error || (!isLoading && !isFetching && (!brandData || !brandData.brand))) {
        return (
            <div className="h-screen flex items-center justify-center">
                <div className="text-center px-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Marque introuvable</h2>
                    <p className="text-gray-500 mb-4">Cette page de marque n'existe pas.</p>
                    <Button onClick={() => navigate("/search")} className="bg-amber-600 hover:bg-amber-700 text-white rounded-full">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Retour
                    </Button>
                </div>
            </div>
        );
    }

    const { brand, stats, userState, topProducts } = brandData;

    return (
        <div className="min-h-screen bg-gradient-to-b from-white via-amber-50/20 to-white dark:from-[hsl(228,14%,6%)] dark:via-[hsl(228,12%,8%)] dark:to-[hsl(225,10%,9%)] pb-20 overflow-x-hidden w-full max-w-full">
            {/* Back button */}
            <div className="px-4 pt-3">
                <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-amber-600 transition-colors">
                    <ArrowLeft className="w-4 h-4" /> Retour
                </button>
            </div>

            {/* Hero */}
            <BrandHero
                brand={brand}
                stats={stats}
                userState={userState}
                onToggleSubscription={handleToggleSubscription}
                isSubscribing={subscribeMutation.isPending || unsubscribeMutation.isPending}
            />

            {/* Divider */}
            <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mx-4 mb-3" />

            {/* Radar Nouveautés */}
            {userState.isSubscribed && stats.newSinceLastVisit > 0 && (
                <NewProductsRadar count={stats.newSinceLastVisit} />
            )}

            {/* Top Pépites Carousel */}
            <TopProductsCarousel products={topProducts} />

            {/* Filter Bar */}
            <div className="flex items-center gap-2 px-4 mb-3 overflow-x-auto scrollbar-hide">
                <MySizeFilterButton
                    user={user}
                    isAuthenticated={isAuthenticated}
                    isActive={mySizesOnly}
                    onToggle={() => setMySizesOnly(!mySizesOnly)}
                    disabled={isLoading || isFetching}
                />
                <button
                    onClick={() => setOnSaleOnly(!onSaleOnly)}
                    disabled={isLoading || isFetching}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all flex-shrink-0 ${
                        onSaleOnly
                            ? "bg-green-600 text-white border-green-600 shadow-md"
                            : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-green-300 dark:hover:border-green-600"
                    } ${(isLoading || isFetching) ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                    <Tag className="w-3 h-3" /> En promo
                </button>
                <button
                    onClick={() => setShowFilters(true)}
                    disabled={isLoading || isFetching}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-amber-300 dark:hover:border-amber-600 transition-all flex-shrink-0"
                >
                    <Filter className="w-3 h-3" /> Filtres
                    {(filters.categories.length > 0 || filters.subcategories.length > 0 || filters.colors.length > 0 || filters.sizes.length > 0 || filters.maxPrice < 10000) && (
                        <span className="w-2 h-2 bg-amber-600 rounded-full" />
                    )}
                </button>
                <div className="ml-auto text-xs text-gray-400 flex-shrink-0">
                    {brandData.totalCount} produit{brandData.totalCount !== 1 ? "s" : ""}
                </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-100 mx-4 mb-4">
                {[
                    { key: "new", label: "Nouveautés", icon: Sparkles },
                    { key: "all", label: "Catalogue", icon: ShoppingBag },
                ].map(({ key, label, icon: Icon }) => (
                    <button
                        key={key}
                        onClick={() => setActiveTab(key)}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-semibold transition-all border-b-2 ${
                            activeTab === key
                                ? "text-amber-600 border-amber-600"
                                : "text-gray-400 border-transparent hover:text-gray-600"
                        }`}
                    >
                        <Icon className="w-3.5 h-3.5" /> {label}
                    </button>
                ))}
            </div>

            {/* Product Grid */}
            <div className="px-4">
                {allProducts.length === 0 && !isLoading ? (
                    <div className="text-center py-12">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                            <ShoppingBag className="w-7 h-7 text-gray-300" />
                        </div>
                        <p className="text-gray-500 text-sm">
                            {activeTab === "new" ? "Pas de nouveautés pour le moment" : "Aucun produit trouvé"}
                        </p>
                        {(mySizesOnly || onSaleOnly) && (
                            <button
                                onClick={() => { setMySizesOnly(false); setOnSaleOnly(false); }}
                                className="mt-2 text-amber-600 text-sm font-medium hover:underline"
                            >
                                Réinitialiser les filtres
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {allProducts.map((product) => (
                            <MatchCard
                                key={product.id}
                                product={product}
                                isNew={product.is_new}
                                source="brand"
                            />
                        ))}
                    </div>
                )}

                {/* Infinite scroll trigger */}
                <div ref={loadMoreRef} className="h-16 flex items-center justify-center">
                    {isLoading && page > 1 && (
                        <div className="w-8 h-8 border-3 border-amber-600 border-t-transparent rounded-full animate-spin" />
                    )}
                    {!hasMore && allProducts.length > 0 && (
                        <p className="text-xs text-gray-400">Vous avez tout vu ✨</p>
                    )}
                </div>
            </div>

            <Dialog open={showFilters} onOpenChange={setShowFilters}>
                <DialogContent className="sm:max-w-md p-0">
                    <FilterPanel
                        filters={filters}
                        setFilters={setFilters}
                        onClose={() => setShowFilters(false)}
                        products={allProducts}
                        hideBrands={true}
                        availableOptions={{
                            ...metadata,
                            categoryRules: rules
                        }}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}
